import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";

export interface EmailPreferences {
  id: string;
  user_id: string;
  notify_announcements: boolean;
  notify_new_courses: boolean;
  notify_discussion_replies: boolean;
  notify_connection_requests: boolean;
  notify_help_requests: boolean;
  notify_messages: boolean;
  notify_admin_new_signups: boolean;
  notify_admin_topshop_applications: boolean;
  notify_admin_call_cancellations: boolean;
  notify_admin_stale_connections: boolean;
  notify_admin_topshop_submissions: boolean;
  created_at: string;
  updated_at: string;
}

export function useEmailPreferences() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["email-preferences", user?.id],
    queryFn: async (): Promise<EmailPreferences | null> => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("user_email_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching email preferences:", error);
        throw error;
      }

      // If no preferences exist, create default ones
      if (!data) {
        const { data: newPrefs, error: insertError } = await supabase
          .from("user_email_preferences")
          .insert({ user_id: user.id })
          .select()
          .single();

        if (insertError) {
          console.error("Error creating email preferences:", insertError);
          throw insertError;
        }

        return newPrefs as EmailPreferences;
      }

      return data as EmailPreferences;
    },
    enabled: !!user?.id,
  });
}

export function useUpdateEmailPreferences() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (updates: Partial<Omit<EmailPreferences, "id" | "user_id" | "created_at" | "updated_at">>) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("user_email_preferences")
        .update(updates)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-preferences"] });
      toast({
        title: "Preferences updated",
        description: "Your email notification preferences have been saved.",
      });
    },
    onError: (error) => {
      console.error("Error updating email preferences:", error);
      toast({
        title: "Error",
        description: "Failed to update preferences. Please try again.",
        variant: "destructive",
      });
    },
  });
}